/**
 * A vector bundle's rooms as a thumbnail, the ones that failed a check in red.
 * Shared by the digitization preview and the plan proposal dialog.
 */
import { useMemo } from 'react'

/** Every room outline, scaled into a fixed box; the failing ones in red. */
export function PlanPreviewThumbnail({ spaces, broken, testId }: {
    spaces: { id: string; name?: string; boundary?: { x: number; y: number }[] }[]
    broken: ReadonlySet<string>
    testId: string
}) {
    const box = useMemo(() => {
        let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity
        for (const sp of spaces) {
            for (const p of sp.boundary ?? []) {
                x0 = Math.min(x0, p.x); y0 = Math.min(y0, p.y)
                x1 = Math.max(x1, p.x); y1 = Math.max(y1, p.y)
            }
        }
        return Number.isFinite(x0) && x1 > x0 && y1 > y0 ? { x0, y0, w: x1 - x0, h: y1 - y0 } : null
    }, [spaces])
    if (!box) return null
    const stroke = Math.max(box.w, box.h) / 300
    return (
        <svg
            viewBox={`${box.x0} ${box.y0} ${box.w} ${box.h}`}
            className="h-48 w-full rounded-md border border-border bg-background"
            preserveAspectRatio="xMidYMid meet"
            data-testid={testId}
        >
            {spaces.map((sp) => (
                <polygon
                    key={sp.id}
                    points={(sp.boundary ?? []).map((p) => `${p.x},${p.y}`).join(' ')}
                    className={broken.has(sp.id)
                        ? 'fill-destructive/25 stroke-destructive'
                        : 'fill-primary/10 stroke-primary'}
                    strokeWidth={stroke}
                    data-broken={broken.has(sp.id) ? 'true' : undefined}
                >
                    <title>{sp.name || sp.id}</title>
                </polygon>
            ))}
        </svg>
    )
}
